import { useEffect, useState } from 'react';

import { IconBrandLinkedin, IconMail, IconPhone } from '@tabler/icons-react';

import { useNavigate, useSearch } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';

import {
  Anchor,
  Box,
  Button,
  Center,
  Container,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@optiro/ui';

import useVerifyAccount from '@/hooks/useVerifyAccount/useVerifyAccount';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import logo from '@/routes/experiences/optiro.svg';

const VerifyAccount = () => {
  const search = useSearch({ from: '/verify-account' });
  const navigate = useNavigate();
  const { mutate, isError, isSuccess, isPending } = useVerifyAccount();
  const [key, setKey] = useState('');
  const { refetchUserData } = useAuth();
  const [isClicked, setIsClicked] = useState(false);

  useEffect(() => {
    if (search.key) {
      setKey(search.key);
    }
  }, [search]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsClicked(true);
    mutate(key);
  };

  useEffect(() => {
    if (isSuccess) {
      refetchUserData();
      navigate({ to: '/app/dashboard' });
    }
    if (isError) {
      setIsClicked(false);
    }
  }, [isSuccess, isError, navigate, refetchUserData]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'rgb(248, 245, 239)' }}>
      {/* Header */}
      <Box
        style={{
          backgroundColor: 'rgb(248, 245, 239)',
        }}
      >
        <Container size="sm">
          <Group justify="center" py="md">
            <Image src={logo} alt="Optiro" h={32} w="auto" />
          </Group>
        </Container>
      </Box>

      <Container size="xs" py="xl">
        <Paper p="xl" radius="md" withBorder shadow="sm" bg="white">
          <Stack gap="md">
            <Title order={2} ta="center" fw={500}>
              Verificer din konto
            </Title>

            <Text size="sm" c="dimmed" ta="center">
              Tryk på knappen nedenfor for at aktivere din konto. Du vil blive sendt videre til
              dashboard efterfølgende.
            </Text>

            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                {isClicked || isPending ? (
                  <Center>
                    <Loader color="#3c735b" />
                  </Center>
                ) : (
                  <Button type="submit" fullWidth size="lg" style={{ backgroundColor: '#3c735b' }}>
                    Verificer konto
                  </Button>
                )}
                {isError && (
                  <Text c="red" size="sm" ta="center">
                    Der opstod en fejl under verifikation
                  </Text>
                )}
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Container>

      <Container size="sm">
        <Group justify="center" gap="xl" py="xl">
          <Anchor
            size="sm"
            c="dimmed"
            href="mailto:support@optiro.dk"
            target="_blank"
            style={{ textDecoration: 'none' }}
          >
            <Group gap={6}>
              <IconMail size={16} />
              support@optiro.dk
            </Group>
          </Anchor>
          <Anchor
            size="sm"
            c="dimmed"
            href="tel:+4526122824"
            target="_blank"
            style={{ textDecoration: 'none' }}
          >
            <Group gap={6}>
              <IconPhone size={16} />
              +45 26 12 28 24
            </Group>
          </Anchor>
          <Anchor
            size="sm"
            c="dimmed"
            href="https://linkedin.com/company/optiro-planning"
            target="_blank"
            style={{ textDecoration: 'none' }}
          >
            <Group gap={6}>
              <IconBrandLinkedin size={16} />
              LinkedIn
            </Group>
          </Anchor>
        </Group>
      </Container>
    </div>
  );
};

export const Route = createFileRoute('/verify-account')({
  component: VerifyAccount,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      key: typeof search.key === 'string' ? search.key : '',
    };
  },
});
